import DOMPurify from "dompurify";
import { navigate } from "@reach/router";
import parse, {
  attributesToProps,
  domToReact,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import { toaster } from "kanopi-components/ToastContainer";
import { StyledLink as Link } from "kanopi-components/Link/StyledLink";
import * as React from "react";
import psl from "psl";

export const frequencyToString = (frequency: string | undefined = ""): string => {
  return (
    {
      MONTHLY: "PER MONTH",
      YEARLY: "PER YEAR",
    }[frequency?.toUpperCase()] || ""
  );
};

export const handleErrorDisplay = (
  exception,
  message = undefined,
  showFieldValidationErrors = false,
) => {
  const displayable_errors = new Set();
  const errors = [];

  if (exception.response) {
    if (Object.keys(exception.response?.errors).includes("__all__")) {
      displayable_errors.add(exception.response.errors["__all__"]);
    } else {
      exception.response?.errors.forEach((error) => {
        if (error?.details && !!Object.keys(error.details).length) {
          if (Object.keys(error.details).includes("__all__")) {
            // Form level errors.
            displayable_errors.add(error.details.__all__.message);
          } else if (showFieldValidationErrors) {
            // Form field level errors.
            displayable_errors.add(error.message);
          }
        } else {
          // Non-form related errors
          displayable_errors.add(error.message);
        }
      });
    }
  } else if (exception.message) {
    // System level errors.
    displayable_errors.add(message || "Something went wrong! We've notified the team.");
    errors.push(exception.message);
  }

  if (errors.length) {
    console.error("Handled exception, errors:", errors, "from", exception);
  }

  if (displayable_errors.size) {
    toaster({ kind: "ERROR", content: displayable_errors });
  }
};

export const gotoPreviousPage = () => {
  navigate(-1);
};

export const gotoParentPage = () => {
  navigate(".");
};

export const getCookieValue = (name: string) => {
  const cookie =
    document?.cookie
      ?.split("; ")
      .find((cookie) => cookie.startsWith(`${name}=`))
      ?.substring(name.length + 1) || ""; // Cut off the name= part and return the rest
  return cookie;
};

export const parseAndPurifyHtml = (htmlString: string) => {
  const options: HTMLReactParserOptions = {
    USE_PROFILES: { html: true },
    replace: (domNode: Element) => {
      const children = domNode.children;
      if (domNode instanceof Element && domNode.attribs && domNode.name === "a") {
        const props = attributesToProps(domNode.attribs);
        return React.createElement(Link, { ...props }, domToReact(children));
      }
    },
  };
  return parse(DOMPurify.sanitize(htmlString), options);
};

export const getNumberWithOrdinal = (n: number) => {
  // Adds ordinals to numbers, e.g. 1 becomes 1st, 2 becomes 2nd. Useful for dates.
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getSubdomain = () => {
  const hostname = window.location.hostname;

  // Parse the domain using the public suffix list
  const parsed = psl.parse(hostname);

  // Type guard - handle the case when parsing fails
  // If no subdomain, or it's just 'www', return null
  if ("error" in parsed || !parsed.subdomain || parsed.subdomain === "www") {
    return null;
  }

  return parsed.subdomain
    .replace(/-/g, " ")
    .replace(/(?:^|\s)[a-z]/g, (c) => c.toUpperCase());
};
